import React, { useState, useEffect } from 'react';
import propTypes from 'prop-types';
import { connect } from 'react-redux';
import { useSearchParams } from 'react-router-dom';
import moment from 'moment';

import { setBreadcrumbs } from 'src/ui';
import { getToken } from 'src/auth';
import { getUser } from 'src/user';
import { getCryptoDecimals } from 'src/core/const';
import { getFIs, fetchFIs } from 'src/financial-institution';
import { api } from 'src/api/config';
import {
  fetchTransactionSummary,
  fetchTransactionList,
  fetchFITransactionSummary,
  fetchFITransactionList,
  fetchAllTransactionsSummary,
  fetchAllTransactionsList,
} from '../redux/actions';
import { fetchDashboardAlerts } from '../../dashboard/services/reportingApi';
import {
  AMOUNT_DISPLAY_THRESHOLD,
  parseOptionalFloat,
  getRawSettlementUsd,
  getSettlementUsd,
  getSettlementStatus,
  getMerchantSettledUsd,
  getMerchantShortfallUsd,
  getCryptoMismatchUsd,
  getNetPayout,
} from '../utils/transactionUtils';

import TransactionDrawer from '../components/TransactionDrawer';
import './TransactionsListPage.css';

// Date Range Presets
const DATE_PRESETS = {
  TODAY: 'today',
  YESTERDAY: 'yesterday',
  LAST_7_DAYS: 'last_7_days',
  MTD: 'mtd',
  LAST_30_DAYS: 'last_30_days',
  LAST_90_DAYS: 'last_90_days',
};

const DATE_PRESET_LABELS = {
  [DATE_PRESETS.TODAY]: 'Today',
  [DATE_PRESETS.YESTERDAY]: 'Yesterday',
  [DATE_PRESETS.LAST_7_DAYS]: 'Last 7 Days',
  [DATE_PRESETS.MTD]: 'Month to Date',
  [DATE_PRESETS.LAST_30_DAYS]: 'Last 30 Days',
  [DATE_PRESETS.LAST_90_DAYS]: 'Last 90 Days',
};

function calculateDateRange(preset) {
  const today = moment().endOf('day').toDate();
  let startDate;

  switch (preset) {
    case DATE_PRESETS.TODAY:
      startDate = moment().startOf('day').toDate();
      break;
    case DATE_PRESETS.YESTERDAY:
      startDate = moment().subtract(1, 'days').startOf('day').toDate();
      return { startDate, endDate: moment().subtract(1, 'days').endOf('day').toDate() };
    case DATE_PRESETS.LAST_7_DAYS:
      startDate = moment().subtract(6, 'days').startOf('day').toDate();
      break;
    case DATE_PRESETS.MTD:
      startDate = moment().startOf('month').toDate();
      break;
    case DATE_PRESETS.LAST_30_DAYS:
      startDate = moment().subtract(29, 'days').startOf('day').toDate();
      break;
    case DATE_PRESETS.LAST_90_DAYS:
      startDate = moment().subtract(89, 'days').startOf('day').toDate();
      break;
    default:
      startDate = moment().startOf('month').toDate();
  }

  return { startDate, endDate: today };
}

// Status configurations (updated for new status model)
// Note: 'needs_resolution' replaces 'underfunded'/'overfunded' as status values
// The discrepancy_type field now tracks whether it's underfunded/overfunded
const STATUS_OPTIONS = [
  { value: 'paid', label: 'Paid', className: 'status-chip--paid' },
  { value: 'pending', label: 'Pending', className: 'status-chip--pending' },
  { value: 'needs_resolution', label: 'Needs Resolution', className: 'status-chip--needs-resolution' },
  { value: 'denied', label: 'Denied', className: 'status-chip--denied' },
  { value: 'refund_pending', label: 'Refund Pending', className: 'status-chip--refund-pending' },
  { value: 'duplicate_payment', label: 'Duplicate Payment', className: 'status-chip--duplicate-payment' },
  { value: 'expired', label: 'Expired', className: 'status-chip--expired' },
  { value: 'refunded', label: 'Refunded', className: 'status-chip--refunded' },
  { value: 'failed', label: 'Failed', className: 'status-chip--failed' },
  { value: 'late', label: 'Late', className: 'status-chip--late', isSpecial: true },
];

// Statuses that have discrepancy amounts to display
// Note: paid with discrepancy_type !== 'none' also shows discrepancy info
const DISCREPANCY_STATUSES = ['needs_resolution', 'denied', 'refund_pending', 'refunded'];

// Icons as inline SVGs
const Icons = {
  Calendar: () => (
    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <rect x="3" y="4" width="18" height="18" rx="2" ry="2" />
      <line x1="16" y1="2" x2="16" y2="6" />
      <line x1="8" y1="2" x2="8" y2="6" />
      <line x1="3" y1="10" x2="21" y2="10" />
    </svg>
  ),
  ChevronDown: () => (
    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <polyline points="6 9 12 15 18 9" />
    </svg>
  ),
  Search: () => (
    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="11" cy="11" r="8" />
      <line x1="21" y1="21" x2="16.65" y2="16.65" />
    </svg>
  ),
  X: () => (
    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <line x1="18" y1="6" x2="6" y2="18" />
      <line x1="6" y1="6" x2="18" y2="18" />
    </svg>
  ),
  Receipt: () => (
    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M4 2v20l2-1 2 1 2-1 2 1 2-1 2 1 2-1 2 1V2l-2 1-2-1-2 1-2-1-2 1-2-1-2 1-2-1z" />
      <line x1="8" y1="6" x2="16" y2="6" />
      <line x1="8" y1="10" x2="16" y2="10" />
      <line x1="8" y1="14" x2="12" y2="14" />
    </svg>
  ),
  ExternalLink: () => (
    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6" />
      <polyline points="15 3 21 3 21 9" />
      <line x1="10" y1="14" x2="21" y2="3" />
    </svg>
  ),
  SortAsc: () => (
    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" width="14" height="14">
      <polyline points="18 15 12 9 6 15" />
    </svg>
  ),
  SortDesc: () => (
    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" width="14" height="14">
      <polyline points="6 9 12 15 18 9" />
    </svg>
  ),
  Refresh: () => (
    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <polyline points="23 4 23 10 17 10" />
      <polyline points="1 20 1 14 7 14" />
      <path d="M3.51 9a9 9 0 0 1 14.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0 0 20.49 15" />
    </svg>
  ),
};

// Typeahead Component - Inline autocomplete suggestions
function SearchableDropdown({ options, value, onChange, placeholder, loading, disabled, wide }) {
  const [isFocused, setIsFocused] = React.useState(false);
  const [searchTerm, setSearchTerm] = React.useState('');
  const [highlightIndex, setHighlightIndex] = React.useState(0);
  const containerRef = React.useRef(null);
  const inputRef = React.useRef(null);

  // Close when clicking outside
  React.useEffect(() => {
    const handleClickOutside = (event) => {
      if (containerRef.current && !containerRef.current.contains(event.target)) {
        setIsFocused(false);
        setSearchTerm('');
        setHighlightIndex(0);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Filter and sort options
  const filteredOptions = React.useMemo(() => {
    const sorted = [...options].sort((a, b) =>
      (a.label || '').toLowerCase().localeCompare((b.label || '').toLowerCase())
    );
    if (!searchTerm) return sorted;
    return sorted.filter(opt =>
      (opt.label || '').toLowerCase().includes(searchTerm.toLowerCase())
    );
  }, [options, searchTerm]);

  // Reset highlight when filtered options change
  React.useEffect(() => {
    setHighlightIndex(0);
  }, [filteredOptions.length]);

  const selectedOption = options.find(opt => opt.value === value);
  const firstMatch = filteredOptions[highlightIndex];

  // Get the autocomplete ghost text
  const getGhostText = () => {
    if (!searchTerm || !firstMatch) return '';
    const label = firstMatch.label || '';
    const matchIndex = label.toLowerCase().indexOf(searchTerm.toLowerCase());
    if (matchIndex === 0) {
      // If match is at start, show the rest as ghost text
      return label.slice(searchTerm.length);
    }
    return '';
  };

  const handleSelect = (optValue) => {
    onChange(optValue);
    setIsFocused(false);
    setSearchTerm('');
    setHighlightIndex(0);
  };

  const handleInputFocus = () => {
    if (!disabled) {
      setIsFocused(true);
    }
  };

  const handleInputChange = (e) => {
    setSearchTerm(e.target.value);
    setHighlightIndex(0);
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Escape') {
      setIsFocused(false);
      setSearchTerm('');
      setHighlightIndex(0);
      inputRef.current?.blur();
    } else if (e.key === 'Tab' && searchTerm && firstMatch) {
      // Tab accepts the current suggestion
      e.preventDefault();
      handleSelect(firstMatch.value);
    } else if (e.key === 'Enter') {
      if (searchTerm && firstMatch) {
        handleSelect(firstMatch.value);
      } else if (!searchTerm) {
        // Clear selection
        handleSelect('');
      }
    } else if (e.key === 'ArrowDown') {
      e.preventDefault();
      setHighlightIndex(prev => Math.min(prev + 1, filteredOptions.length - 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setHighlightIndex(prev => Math.max(prev - 1, 0));
    }
  };

  const displayValue = isFocused ? searchTerm : (selectedOption?.label || '');
  const ghostText = getGhostText();
  const matchCount = searchTerm ? filteredOptions.length : options.length;

  return (
    <div
      ref={containerRef}
      className={`typeahead ${wide ? 'typeahead--wide' : ''} ${disabled ? 'typeahead--disabled' : ''} ${isFocused ? 'typeahead--focused' : ''}`}
    >
      <div className="typeahead-input-container">
        <Icons.Search />
        <div className="typeahead-input-wrapper">
          <input
            ref={inputRef}
            type="text"
            className="typeahead-input"
            placeholder={loading ? 'Loading...' : (selectedOption?.label || placeholder)}
            value={displayValue}
            onChange={handleInputChange}
            onFocus={handleInputFocus}
            onKeyDown={handleKeyDown}
            disabled={disabled || loading}
          />
          {/* Ghost text for autocomplete suggestion */}
          {isFocused && ghostText && (
            <span className="typeahead-ghost">{searchTerm}<span className="typeahead-ghost-suggestion">{ghostText}</span></span>
          )}
        </div>
        {/* Match count badge */}
        {isFocused && searchTerm && (
          <span className="typeahead-count">{matchCount}</span>
        )}
        {/* Clear button when value selected */}
        {value && !isFocused && (
          <button
            type="button"
            className="typeahead-clear"
            onClick={(e) => { e.stopPropagation(); handleSelect(''); }}
          >
            <Icons.X />
          </button>
        )}
      </div>

      {/* Dropdown list - show when focused */}
      {isFocused && (
        <div className="typeahead-dropdown">
          {/* Clear selection option */}
          {value && (
            <button
              type="button"
              className="typeahead-option typeahead-option--clear"
              onClick={() => handleSelect('')}
            >
              Clear selection
            </button>
          )}

          {/* Options list */}
          {filteredOptions.length > 0 ? (
            <div className="typeahead-options">
              {filteredOptions.map((opt, idx) => (
                <button
                  key={opt.value}
                  type="button"
                  className={`typeahead-option ${idx === highlightIndex ? 'typeahead-option--highlighted' : ''} ${opt.value === value ? 'typeahead-option--selected' : ''}`}
                  onClick={() => handleSelect(opt.value)}
                  onMouseEnter={() => setHighlightIndex(idx)}
                >
                  {opt.label}
                </button>
              ))}
            </div>
          ) : (
            <div className="typeahead-empty">No matches for "{searchTerm}"</div>
          )}
        </div>
      )}
    </div>
  );
}

SearchableDropdown.propTypes = {
  options: propTypes.arrayOf(propTypes.shape({
    value: propTypes.oneOfType([propTypes.string, propTypes.number]).isRequired,
    label: propTypes.string.isRequired,
  })).isRequired,
  value: propTypes.oneOfType([propTypes.string, propTypes.number]),
  onChange: propTypes.func.isRequired,
  placeholder: propTypes.string,
  loading: propTypes.bool,
  disabled: propTypes.bool,
  wide: propTypes.bool,
};

// Metric Card Component
function MetricCard({ label, value, subtitle, variant, clickable, onClick }) {
  return (
    <div
      className={`metric-card metric-card--${variant || 'primary'} ${clickable ? 'clickable' : ''}`}
      onClick={clickable ? onClick : undefined}
      role={clickable ? 'button' : undefined}
      tabIndex={clickable ? 0 : undefined}
    >
      <div className="metric-label">{label}</div>
      <div className="metric-value">{value}</div>
      {subtitle && <div className="metric-subtitle">{subtitle}</div>}
    </div>
  );
}

MetricCard.propTypes = {
  label: propTypes.string.isRequired,
  value: propTypes.oneOfType([propTypes.string, propTypes.number]).isRequired,
  subtitle: propTypes.string,
  variant: propTypes.string,
  clickable: propTypes.bool,
  onClick: propTypes.func,
};

// Inline Date Range Picker Component
function DateRangePicker({ startDate, endDate, activePreset, onDateChange, onPresetSelect }) {
  const formatDateForInput = (date) => {
    if (!date) return '';
    return moment(date).format('YYYY-MM-DD');
  };

  const handleStartChange = (e) => {
    const newStart = e.target.value ? moment(e.target.value).startOf('day').toDate() : null;
    onDateChange(newStart, endDate);
  };

  const handleEndChange = (e) => {
    const newEnd = e.target.value ? moment(e.target.value).endOf('day').toDate() : null;
    onDateChange(startDate, newEnd);
  };

  const presetButtons = [
    { key: DATE_PRESETS.TODAY, label: 'Today' },
    { key: DATE_PRESETS.LAST_7_DAYS, label: '7D' },
    { key: DATE_PRESETS.LAST_30_DAYS, label: '30D' },
    { key: DATE_PRESETS.MTD, label: 'MTD' },
  ];

  return (
    <div className="date-range-picker">
      <div className="date-inputs">
        <div className="date-input-wrapper">
          <Icons.Calendar />
          <input
            type="date"
            className="date-input"
            value={formatDateForInput(startDate)}
            onChange={handleStartChange}
          />
        </div>
        <span className="date-separator">→</span>
        <div className="date-input-wrapper">
          <input
            type="date"
            className="date-input"
            value={formatDateForInput(endDate)}
            onChange={handleEndChange}
          />
        </div>
      </div>
      <div className="date-presets">
        {presetButtons.map(({ key, label }) => (
          <button
            key={key}
            type="button"
            className={`preset-pill ${activePreset === key ? 'active' : ''}`}
            onClick={() => onPresetSelect(key)}
          >
            {label}
          </button>
        ))}
      </div>
    </div>
  );
}

DateRangePicker.propTypes = {
  startDate: propTypes.instanceOf(Date),
  endDate: propTypes.instanceOf(Date),
  activePreset: propTypes.string,
  onDateChange: propTypes.func.isRequired,
  onPresetSelect: propTypes.func.isRequired,
};


// Main Component
const TransactionsListPage = ({
  setBreadcrumbs,
  token,
  user,
  financialInstitutions,
  fetchFIs,
  summaryData,
  summaryLoading,
  summaryError,
  listData,
  listLoading,
  listError,
  fetchTransactionSummary,
  fetchTransactionList,
  fetchFITransactionSummary,
  fetchFITransactionList,
  fetchAllTransactionsSummary,
  fetchAllTransactionsList,
  externalDateRange,
  hideDatePicker,
  showInlineHeader,
}) => {
  const [searchParams] = useSearchParams();
  const urlFiId = searchParams.get('fi_id');
  const urlAccountId = searchParams.get('account_id');
  const urlStatus = searchParams.get('status');
  const urlLate = searchParams.get('late');

  const [currentPage, setCurrentPage] = React.useState(1);
  const [merchantAccounts, setMerchantAccounts] = React.useState([]);
  const [loadingMerchants, setLoadingMerchants] = React.useState(false);
  const [selectedTransaction, setSelectedTransaction] = React.useState(null);
  const [drawerOpen, setDrawerOpen] = React.useState(false);
  const [sortConfig, setSortConfig] = React.useState({ key: null, direction: 'asc' });
  const [dashboardAlerts, setDashboardAlerts] = React.useState({ alerts: [], summary: {} });

  // Determine user role
  const isSuperAdmin = user?.isSuperAdmin || user?.isSolventAdmin;
  const isFIAdmin = user?.isBankAdmin || user?.isPrimaryBankAdmin;
  const isMerchantAdmin = user?.isMerchant && !isSuperAdmin && !isFIAdmin;

  // Initial FI selection - Merchant Admin always uses their own FI/Account
  const initialFiId = isMerchantAdmin
    ? String(user?.financialInstitutionId || '')
    : urlFiId || (isFIAdmin && user?.financialInstitutionId ? String(user.financialInstitutionId) : '');
  const initialMerchantId = isMerchantAdmin
    ? String(user?.accountId || '')
    : urlAccountId || '';

  // FI Admins see actionable statuses (paid, needs_resolution) plus refunded/failed
  // They don't need detailed discrepancy info, just the financial summary
  const statusOptions = (isFIAdmin && !isSuperAdmin)
    ? STATUS_OPTIONS.filter((option) => ['paid', 'needs_resolution', 'refund_pending', 'denied', 'refunded', 'failed'].includes(option.value))
    : STATUS_OPTIONS;
  const statusOptionValues = statusOptions.map((option) => option.value);

  // Determine initial status filter from URL or default
  // Default: paid, needs_resolution (filtered to valid options for user's role)
  const defaultStatuses = ['paid', 'needs_resolution', 'refund_pending']
    .filter((status) => statusOptionValues.includes(status));
  const initialStatus = urlStatus
    ? urlStatus.split(',').filter((status) => statusOptionValues.includes(status))
    : defaultStatuses;
  const initialLateOnly = urlLate === 'true';

  // Filter state
  const [filters, setFilters] = React.useState({
    datePreset: DATE_PRESETS.MTD,
    customStartDate: null,
    customEndDate: null,
    status: initialStatus,
    lateOnly: initialLateOnly,  // Filter for late payments only
    searchSessionId: '',
    searchTxHash: '',
    fiId: initialFiId,
    merchantId: initialMerchantId,
  });

  // Refresh key to trigger data refetch
  const [refreshKey, setRefreshKey] = React.useState(0);
  const handleRefreshData = React.useCallback(() => {
    setRefreshKey((prev) => prev + 1);
  }, []);

  // Calculate date range
  const dateRange = React.useMemo(() => {
    if (externalDateRange) {
      return externalDateRange;
    }
    // Use custom dates if set, otherwise use preset
    if (filters.customStartDate && filters.customEndDate) {
      return [filters.customStartDate, filters.customEndDate];
    }
    const range = calculateDateRange(filters.datePreset);
    return [range.startDate, range.endDate];
  }, [filters.datePreset, filters.customStartDate, filters.customEndDate, externalDateRange]);

  // Create stable string versions for useEffect dependencies
  const dateFromStr = dateRange[0]?.toISOString();
  const dateToStr = dateRange[1]?.toISOString();
  const statusStr = filters.status.join(',');
  const userFiId = user?.financialInstitutionId;
  const userAccountId = user?.accountId;
  const accessToken = token?.accessToken;

  // Fetch FIs on mount (for super admins)
  React.useEffect(() => {
    if (isSuperAdmin) {
      fetchFIs();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isSuperAdmin]);

  // Fetch dashboard alerts for SuperAdmin
  React.useEffect(() => {
    if (isSuperAdmin && accessToken) {
      fetchDashboardAlerts(accessToken)
        .then(setDashboardAlerts)
        .catch((err) => console.error('Failed to fetch dashboard alerts:', err));
    }
  }, [isSuperAdmin, accessToken]);

  // Set breadcrumbs
  React.useEffect(() => {
    setBreadcrumbs([{ label: 'Transactions', href: '/solventpay/transactions' }]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Fetch data when filters change
  React.useEffect(() => {
    if (!accessToken) return;
    if (!token?.isValid?.() || !user?.isValid?.()) return;

    const dateFrom = dateFromStr;
    const dateTo = dateToStr;
    const effectiveFiId = filters.fiId || userFiId;
    const effectiveAccountId = isMerchantAdmin ? userAccountId : filters.merchantId;

    if (!effectiveFiId && !isSuperAdmin) return;

    const fetchSearchParams = {};
    if (filters.searchSessionId) fetchSearchParams.search = filters.searchSessionId;
    if (filters.searchTxHash) fetchSearchParams.tx_hash = filters.searchTxHash;
    if (filters.lateOnly) fetchSearchParams.was_late_payment = 'true';

    // Fetch summary and list based on user role and filters
    if (isMerchantAdmin && effectiveFiId && effectiveAccountId) {
      // Merchant Admin - fetch only their own account's transactions
      fetchTransactionSummary({ fi: effectiveFiId, account: effectiveAccountId, dateFrom, dateTo, status: filters.status });
      fetchTransactionList({
        fi: effectiveFiId,
        account: effectiveAccountId,
        page: currentPage,
        pageSize: 50,
        dateFrom,
        dateTo,
        status: statusStr,
        ...fetchSearchParams,
      });
    } else if (isSuperAdmin && !effectiveFiId) {
      // Super Admin with no FI selected - fetch all transactions
      fetchAllTransactionsSummary({ dateFrom, dateTo, status: filters.status });
      fetchAllTransactionsList({
        page: currentPage,
        pageSize: 50,
        dateFrom,
        dateTo,
        status: statusStr,
        ...fetchSearchParams,
      });
    } else if (effectiveFiId) {
      // FI Admin or Super Admin with FI selected
      fetchFITransactionSummary({ fi: effectiveFiId, dateFrom, dateTo, status: filters.status });
      fetchFITransactionList({
        fi: effectiveFiId,
        page: currentPage,
        pageSize: 50,
        dateFrom,
        dateTo,
        status: statusStr,
        ...fetchSearchParams,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    dateFromStr,
    dateToStr,
    currentPage,
    statusStr,
    filters.searchSessionId,
    filters.searchTxHash,
    filters.lateOnly,
    filters.fiId,
    filters.merchantId,
    userFiId,
    userAccountId,
    isSuperAdmin,
    isMerchantAdmin,
    accessToken,
    refreshKey,
  ]);

  // Load merchants - for SuperAdmin load all or by FI, for FI Admin load their FI's merchants
  // Skip for Merchant Admin (they only see their own transactions)
  React.useEffect(() => {
    if (!accessToken) return;
    if (isMerchantAdmin) {
      // Merchant Admin doesn't need merchant selection - skip loading
      setMerchantAccounts([]);
      return;
    }

    // Determine which FI to load merchants for
    const fiToLoad = filters.fiId || (isFIAdmin ? userFiId : null);

    if (fiToLoad) {
      // Load merchants for specific FI
      setLoadingMerchants(true);
      fetch(`${api.hostname}/api/v1/transactions/fi-merchants/${fiToLoad}/`, {
        headers: { Authorization: `Bearer ${accessToken}` },
      })
        .then((res) => (res.ok ? res.json() : []))
        .then((data) => {
          const mapped = (data || []).map((m) => ({
            id: m.terra_account_id || m.id,
            businessName: m.business_name || m.businessName || m.name,
          }));
          setMerchantAccounts(mapped);
        })
        .catch(() => setMerchantAccounts([]))
        .finally(() => setLoadingMerchants(false));
    } else if (isSuperAdmin) {
      // SuperAdmin with no FI selected - load all merchants
      setLoadingMerchants(true);
      fetch(`${api.hostname}/api/v1/transactions/all-merchants/`, {
        headers: { Authorization: `Bearer ${accessToken}` },
      })
        .then((res) => (res.ok ? res.json() : []))
        .then((data) => {
          const mapped = (data || []).map((m) => ({
            id: m.terra_account_id || m.id,
            businessName: m.business_name || m.businessName || m.name,
            fiName: m.fi_name || m.financialInstitutionName,
          }));
          setMerchantAccounts(mapped);
        })
        .catch(() => setMerchantAccounts([]))
        .finally(() => setLoadingMerchants(false));
    } else {
      setMerchantAccounts([]);
    }
  }, [filters.fiId, accessToken, isSuperAdmin, isFIAdmin, isMerchantAdmin, userFiId]);

  // Handlers
  const handlePresetSelect = (preset) => {
    setFilters((prev) => ({
      ...prev,
      datePreset: preset,
      customStartDate: null,
      customEndDate: null,
    }));
    setCurrentPage(1);
  };

  const handleCustomDateChange = (startDate, endDate) => {
    setFilters((prev) => ({
      ...prev,
      datePreset: null,
      customStartDate: startDate,
      customEndDate: endDate,
    }));
    setCurrentPage(1);
  };

  const handleStatusToggle = (status) => {
    // Handle "late" filter specially - it's a separate query param
    if (status === 'late') {
      setFilters((prev) => ({ ...prev, lateOnly: !prev.lateOnly }));
      setCurrentPage(1);
      return;
    }

    setFilters((prev) => {
      const newStatus = prev.status.includes(status)
        ? prev.status.filter((s) => s !== status)
        : [...prev.status, status];
      return { ...prev, status: newStatus };
    });
    setCurrentPage(1);
  };

  const handleSearchChange = (e) => {
    setFilters((prev) => ({ ...prev, searchSessionId: e.target.value }));
  };

  const handleSearchKeyDown = (e) => {
    if (e.key === 'Enter') {
      setCurrentPage(1);
    }
  };

  const handleFiChange = (e) => {
    setFilters((prev) => ({ ...prev, fiId: e.target.value, merchantId: '' }));
    setCurrentPage(1);
  };

  const handleMerchantChange = (e) => {
    setFilters((prev) => ({ ...prev, merchantId: e.target.value }));
    setCurrentPage(1);
  };

  const handleRowClick = (transaction) => {
    setSelectedTransaction(transaction);
    setDrawerOpen(true);
  };

  const handleCloseDrawer = () => {
    setDrawerOpen(false);
    setTimeout(() => setSelectedTransaction(null), 300);
  };

  const handleSort = (key) => {
    setSortConfig((prev) => ({
      key,
      direction: prev.key === key && prev.direction === 'asc' ? 'desc' : 'asc',
    }));
  };

  // Sort transactions
  const sortedTransactions = React.useMemo(() => {
    const txList = listData?.results || [];
    if (!sortConfig.key) return txList;

    return [...txList].sort((a, b) => {
      let aVal, bVal;
      if (sortConfig.key === 'order_amount_usd') {
        aVal = parseFloat(a.order_amount_usd || 0);
        bVal = parseFloat(b.order_amount_usd || 0);
      } else if (sortConfig.key === 'status') {
        aVal = a.status || '';
        bVal = b.status || '';
      } else if (sortConfig.key === 'created_at') {
        aVal = new Date(a.created_at || 0).getTime();
        bVal = new Date(b.created_at || 0).getTime();
      } else if (sortConfig.key === 'merchant_name') {
        aVal = (a.businessName || a.merchant_name || '').toLowerCase();
        bVal = (b.businessName || b.merchant_name || '').toLowerCase();
      } else if (sortConfig.key === 'fi_name') {
        aVal = (a.fi_name || '').toLowerCase();
        bVal = (b.fi_name || '').toLowerCase();
      } else if (sortConfig.key === 'commission') {
        // Use backend commission_data if available, fallback to calculation
        const getCommission = (tx) => {
          if (tx.commission_data?.fi_revenue_usd) {
            return parseFloat(tx.commission_data.fi_revenue_usd);
          }
          // Fallback for old transactions without commission data
          const totalFees = parseOptionalFloat(tx.actual_total_fees_usd)
            ?? ((parseOptionalFloat(tx.actual_merchant_fee_usd) ?? 0)
              + (parseOptionalFloat(tx.actual_customer_fee_usd) ?? 0));
          const transactionAmount = parseFloat(tx.order_amount_usd || 0);
          const solventBuyRate = (transactionAmount * 0.01) + 0.25;
          const markup = Math.max(0, totalFees - solventBuyRate);
          return markup * 0.6;
        };
        aVal = getCommission(a);
        bVal = getCommission(b);
      } else {
        return 0;
      }

      if (aVal < bVal) return sortConfig.direction === 'asc' ? -1 : 1;
      if (aVal > bVal) return sortConfig.direction === 'asc' ? 1 : -1;
      return 0;
    });
  }, [listData?.results, sortConfig]);

  // Calculate commission for a transaction (for FI Admin view)
  // Uses backend commission_data if available (accurate to actual ISO config)
  // Buffer profit is 100% Solvent's - not shared with ISO
  const calculateCommission = (tx) => {
    // Use backend data if available
    if (tx.commission_data?.fi_revenue_usd) {
      return parseFloat(tx.commission_data.fi_revenue_usd);
    }
    // Fallback for old transactions without commission data
    const totalFees = parseOptionalFloat(tx.actual_total_fees_usd)
      ?? ((parseOptionalFloat(tx.actual_merchant_fee_usd) ?? 0)
        + (parseOptionalFloat(tx.actual_customer_fee_usd) ?? 0));
    const transactionAmount = parseFloat(tx.order_amount_usd || 0);
    const solventBuyRate = (transactionAmount * 0.01) + 0.25;
    const markup = Math.max(0, totalFees - solventBuyRate);
    return markup * 0.6;
  };

  // Format helpers
  const formatCurrency = (value) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 2,
    }).format(value || 0);
  };

  const formatNumber = (value) => {
    return new Intl.NumberFormat('en-US').format(value || 0);
  };

  // Loading state
  if (!user?.isValid || !user.isValid()) {
    return (
      <div className="transactions-dashboard">
        <div className="loading-state">
          <div className="loading-spinner" />
          <div className="loading-text">Loading...</div>
        </div>
      </div>
    );
  }

  const transactions = sortedTransactions;
  const totalCount = listData?.count || 0;

  return (
    <div className="transactions-dashboard">
      {/* Header - only show if not using inline header */}
      {!showInlineHeader && (
        <div className="dashboard-header">
          <div className="header-left">
            <h1 className="page-title">Transactions</h1>
          </div>
          <div className="header-actions">
            <button className="header-btn">
              <Icons.Receipt />
              Export CSV
            </button>
          </div>
        </div>
      )}

      {/* Error State */}
      {(summaryError || listError) && (
        <div className="error-state">
          <span className="error-message">{summaryError || listError}</span>
        </div>
      )}

      {/* Dashboard Alerts for SuperAdmin */}
      {isSuperAdmin && dashboardAlerts.alerts && dashboardAlerts.alerts.length > 0 && (
        <div className="dashboard-alerts">
          {dashboardAlerts.alerts.map((alert, index) => (
            <div key={index} className={`alert-banner alert-banner--${alert.type}`}>
              <div className="alert-content">
                <span className="alert-icon">
                  {alert.type === 'error' ? '❌' : alert.type === 'warning' ? '⚠️' : 'ℹ️'}
                </span>
                <div className="alert-text">
                  <strong>{alert.title}:</strong> {alert.message}
                  {alert.action && <span className="alert-action"> — {alert.action}</span>}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Metrics Grid */}
      <div className="metrics-grid">
        <MetricCard
          label="Total Transactions"
          value={formatNumber(summaryData?.paid_transactions || 0)}
          subtitle="Completed payments"
          variant="primary"
          clickable
          onClick={() => handleStatusToggle('paid')}
        />
        <MetricCard
          label="Total Volume"
          value={formatCurrency(summaryData?.total_usd_volume || summaryData?.total_amount_paid || 0)}
          subtitle="USD processed"
          variant="success"
        />
        <MetricCard
          label={isMerchantAdmin ? 'Fees Paid' : 'Fees Collected'}
          value={formatCurrency(summaryData?.total_fees_collected || 0)}
          subtitle={isMerchantAdmin ? 'Transaction fees' : 'From merchants'}
          variant="warning"
        />
        {(isSuperAdmin || isFIAdmin) && summaryData?.total_commission !== undefined && (
          <MetricCard
            label={isSuperAdmin ? 'ISO Commissions' : 'Your Commission'}
            value={formatCurrency(summaryData?.total_commission || 0)}
            subtitle={isSuperAdmin ? 'Owed to ISOs' : 'Earned this period'}
            variant="purple"
          />
        )}
      </div>

      {/* Filter Bar */}
      <div className="filter-bar-container">
        {/* Top Row: Date Range Picker + Export */}
        <div className="filter-row filter-row--top">
          {/* Date Range Picker */}
          {!hideDatePicker && (
            <DateRangePicker
              startDate={dateRange[0]}
              endDate={dateRange[1]}
              activePreset={filters.datePreset}
              onDateChange={handleCustomDateChange}
              onPresetSelect={handlePresetSelect}
            />
          )}

          <div className="filter-spacer" />

          {/* Refresh button */}
          <button
            className={`header-btn header-btn--icon-only${listLoading || summaryLoading ? ' header-btn--loading' : ''}`}
            onClick={handleRefreshData}
            disabled={listLoading || summaryLoading}
            title="Refresh transactions"
            type="button"
          >
            <Icons.Refresh />
          </button>

          {/* Export CSV button */}
          <button className="header-btn">
            <Icons.Receipt />
            Export CSV
          </button>
        </div>

        {/* Middle Row: Status Chips + Search */}
        <div className="filter-row filter-row--middle">
          {/* Status Chips */}
          <div className="filter-group">
            <div className="status-chips">
              {statusOptions.map((status) => {
                // Special handling for "late" filter
                const isActive = status.value === 'late'
                  ? filters.lateOnly
                  : filters.status.includes(status.value);

                return (
                  <button
                    key={status.value}
                    className={`status-chip ${status.className} ${isActive ? 'active' : ''}`}
                    onClick={() => handleStatusToggle(status.value)}
                    type="button"
                  >
                    <span className="status-chip-dot" />
                    {status.label}
                  </button>
                );
              })}
            </div>
          </div>

          <div className="filter-divider" />

          {/* Search */}
          <div className="search-input-wrapper">
            <Icons.Search />
            <input
              type="text"
              className="search-input"
              placeholder="Search session ID..."
              value={filters.searchSessionId}
              onChange={handleSearchChange}
              onKeyDown={handleSearchKeyDown}
            />
          </div>
        </div>

        {/* Bottom Row: FI + Merchant Dropdowns */}
        {(isSuperAdmin || isFIAdmin) && (
          <div className="filter-row filter-row--bottom">
            {/* FI Select (SuperAdmin only) */}
            {isSuperAdmin && (
              <SearchableDropdown
                options={(financialInstitutions?.items || []).map((fi) => ({
                  value: String(fi.id),
                  label: fi.businessName || fi.name,
                }))}
                value={filters.fiId}
                onChange={(val) => handleFiChange({ target: { value: val } })}
                placeholder="All ISOs"
                wide
              />
            )}

            {/* Merchant Select - available for SuperAdmin and FI Admin */}
            <SearchableDropdown
              options={merchantAccounts.map((merchant) => ({
                value: String(merchant.id),
                label: `${merchant.businessName || merchant.name}${merchant.fiName && !filters.fiId ? ` (${merchant.fiName})` : ''}`,
              }))}
              value={filters.merchantId}
              onChange={(val) => handleMerchantChange({ target: { value: val } })}
              placeholder="All Merchants"
              loading={loadingMerchants}
              wide
            />
          </div>
        )}
      </div>

      {/* Transactions Table */}
      <div className="table-container">
        {listLoading || summaryLoading ? (
          <div className="loading-state">
            <div className="loading-spinner" />
            <div className="loading-text">Loading transactions...</div>
          </div>
        ) : transactions.length === 0 ? (
          <div className="empty-state">
            <div className="empty-icon">
              <Icons.Receipt />
            </div>
            <div className="empty-title">No transactions found</div>
            <div className="empty-description">Try adjusting your filters or date range</div>
          </div>
        ) : (
          <>
            <table className="transactions-table">
              <thead>
                <tr>
                  {/* Session ID - shown for SuperAdmin and Merchant Admin only */}
                  {(isSuperAdmin || isMerchantAdmin) && (
                    <th>Session ID</th>
                  )}
                  {/* FI column - shown only for SuperAdmin when no FI filter */}
                  {isSuperAdmin && !filters.fiId && (
                    <th
                      className="sortable"
                      onClick={() => handleSort('fi_name')}
                    >
                      ISO
                      {sortConfig.key === 'fi_name' && (
                        <span className="sort-icon">
                          {sortConfig.direction === 'asc' ? <Icons.SortAsc /> : <Icons.SortDesc />}
                        </span>
                      )}
                    </th>
                  )}
                  {/* Merchant column - not shown for Merchant Admin */}
                  {!isMerchantAdmin && (
                    <th
                      className="sortable"
                      onClick={() => handleSort('merchant_name')}
                    >
                      Merchant
                      {sortConfig.key === 'merchant_name' && (
                        <span className="sort-icon">
                          {sortConfig.direction === 'asc' ? <Icons.SortAsc /> : <Icons.SortDesc />}
                        </span>
                      )}
                    </th>
                  )}
                  <th
                    className="text-right sortable"
                    onClick={() => handleSort('order_amount_usd')}
                  >
                    Order Amount
                    {sortConfig.key === 'order_amount_usd' && (
                      <span className="sort-icon">
                        {sortConfig.direction === 'asc' ? <Icons.SortAsc /> : <Icons.SortDesc />}
                      </span>
                    )}
                  </th>
                  <th className="text-right">
                    Expected
                  </th>
                  {/* Received column - Merchant Admin and SuperAdmin */}
                  {(isMerchantAdmin || isSuperAdmin) && (
                    <th className="text-right">Received</th>
                  )}
                  {/* Accepted column - Merchant Admin only (for resolved discrepancies) */}
                  {isMerchantAdmin && (
                    <th className="text-right">Accepted</th>
                  )}
                  {/* Fees column - Merchant Admin and FI Admin */}
                  {(isMerchantAdmin || (isFIAdmin && !isSuperAdmin)) && (
                    <th className="text-right">Fees</th>
                  )}
                  {/* Net Payout column - Merchant Admin and FI Admin */}
                  {(isMerchantAdmin || (isFIAdmin && !isSuperAdmin)) && (
                    <th className="text-right">Net Payout</th>
                  )}
                  <th
                    className="sortable"
                    onClick={() => handleSort('status')}
                  >
                    Status
                    {sortConfig.key === 'status' && (
                      <span className="sort-icon">
                        {sortConfig.direction === 'asc' ? <Icons.SortAsc /> : <Icons.SortDesc />}
                      </span>
                    )}
                  </th>
                  {/* Difference column - hidden for FI Admins */}
                  {!isFIAdmin && (
                    <th className="text-right">
                      {isMerchantAdmin ? 'Difference' : 'Discrepancy'}
                    </th>
                  )}
                  {/* Commission column - shown for FI Admin only */}
                  {isFIAdmin && !isSuperAdmin && (
                    <th
                      className="text-right sortable"
                      onClick={() => handleSort('commission')}
                    >
                      Commission
                      {sortConfig.key === 'commission' && (
                        <span className="sort-icon">
                          {sortConfig.direction === 'asc' ? <Icons.SortAsc /> : <Icons.SortDesc />}
                        </span>
                      )}
                    </th>
                  )}
                  <th
                    className="sortable"
                    onClick={() => handleSort('created_at')}
                  >
                    Date
                    {sortConfig.key === 'created_at' && (
                      <span className="sort-icon">
                        {sortConfig.direction === 'asc' ? <Icons.SortAsc /> : <Icons.SortDesc />}
                      </span>
                    )}
                  </th>
                </tr>
              </thead>
              <tbody>
                {transactions.map((tx) => {
                  const settlementStatus = getSettlementStatus(tx);
                  const merchantSettledUsd = getMerchantSettledUsd(tx);
                  const merchantShortfallUsd = getMerchantShortfallUsd(tx);
                  const refundedAmountUsd = parseOptionalFloat(tx.refunded_total_usd);
                  const showMerchantSettlement = isMerchantAdmin
                    && ['paid', 'refund_pending'].includes(tx.status);
                  const showRefundedAmount = isMerchantAdmin
                    && tx.status === 'refunded';
                  const showReceivedEstimate = isMerchantAdmin
                    && !showMerchantSettlement
                    && !showRefundedAmount
                    && ['needs_resolution', 'denied'].includes(tx.status);
                  const hasSettlementShortfall = isMerchantAdmin
                    && tx.status === 'paid'
                    && settlementStatus === 'settled'
                    && merchantShortfallUsd > AMOUNT_DISPLAY_THRESHOLD;
                  const showSettlementPending = isMerchantAdmin
                    && tx.status === 'paid'
                    && settlementStatus !== 'settled';

                  const refundDueUsd = parseOptionalFloat(tx.refund_due_usd) ?? 0;
                  const refundDueSource = tx.refund_due_source || null;
                  const hasRefundDue = refundDueUsd > AMOUNT_DISPLAY_THRESHOLD;

                  // Fully refunded when backend says nothing remains and no pending refund
                  const remainingRefundable = parseOptionalFloat(tx.remaining_refundable_usd) ?? null;
                  const refundedTotalUsd = parseOptionalFloat(tx.refunded_total_usd) ?? 0;
                  const refundedFromPayoutUsd = parseOptionalFloat(tx.refunded_from_payout_usd) ?? 0;
                  const isFullyRefunded = tx.status === 'refunded'
                    || (remainingRefundable !== null && remainingRefundable <= AMOUNT_DISPLAY_THRESHOLD && refundedTotalUsd > AMOUNT_DISPLAY_THRESHOLD && !hasRefundDue);

                  // Manual refunds are those refunded from payout (backend-tracked)
                  const hasCompletedManualRefund = refundedFromPayoutUsd > AMOUNT_DISPLAY_THRESHOLD;
                  const manualRefundsTotal = refundedFromPayoutUsd;

                  return (
                    <tr key={tx.id} onClick={() => handleRowClick(tx)}>
                    {/* Session ID - shown for SuperAdmin and Merchant Admin only */}
                    {(isSuperAdmin || isMerchantAdmin) && (
                      <td className="td-session-id" title={tx.session_id}>
                        {tx.session_id ? `${tx.session_id.substring(0, 8)}...` : '-'}
                      </td>
                    )}
                    {/* FI column - shown only for SuperAdmin when no FI filter */}
                    {isSuperAdmin && !filters.fiId && (
                      <td className="td-fi">
                        {tx.fi_name || '-'}
                      </td>
                    )}
                    {/* Merchant column - not shown for Merchant Admin */}
                    {!isMerchantAdmin && (
                      <td>
                        <div className="td-merchant">
                          <span className="merchant-name">{tx.businessName || tx.merchant_name || '-'}</span>
                        </div>
                      </td>
                    )}
                    <td className="td-amount text-right">
                      {formatCurrency(tx.order_amount_usd)}
                      {isSuperAdmin && tx.expected_crypto && (
                        <div className="td-crypto-subtitle">
                          {parseFloat(tx.expected_crypto).toFixed(getCryptoDecimals(tx.crypto_currency))} {tx.crypto_currency || ''}
                        </div>
                      )}
                    </td>
                    {/* Expected column - what customer should pay (order + customer fees) */}
                    <td className="td-amount text-right">
                      {formatCurrency(
                        tx.expected_payment_usd || tx.order_amount_usd
                      )}
                    </td>
                    {/* Received column - Merchant Admin and SuperAdmin */}
                    {(isMerchantAdmin || isSuperAdmin) && (
                      <td className="td-received text-right">
                        {(isSuperAdmin ? getRawSettlementUsd(tx) : getSettlementUsd(tx)) != null ? (
                          <>
                            {formatCurrency(isSuperAdmin ? getRawSettlementUsd(tx) : getSettlementUsd(tx))}
                            {isSuperAdmin && tx.received_crypto && (
                              <div className="td-crypto-subtitle">
                                {parseFloat(tx.received_crypto).toFixed(getCryptoDecimals(tx.crypto_currency))} {tx.crypto_currency || ''}
                              </div>
                            )}
                          </>
                        ) : (
                          <span className="text-muted">—</span>
                        )}
                      </td>
                    )}
                    {/* Accepted column - Merchant Admin only (shows accepted amount for resolved discrepancies) */}
                    {isMerchantAdmin && (
                      <td className="td-amount text-right">
                        {tx.accepted_amount_usd != null ? (
                          formatCurrency(tx.accepted_amount_usd)
                        ) : tx.status === 'paid' && getSettlementUsd(tx) != null ? (
                          formatCurrency(getSettlementUsd(tx))
                        ) : (
                          <span className="text-muted">—</span>
                        )}
                      </td>
                    )}
                    {/* Fees column - Merchant Admin and FI Admin */}
                    {(isMerchantAdmin || (isFIAdmin && !isSuperAdmin)) && (
                      <td className="td-amount text-right">
                        {(() => {
                          const totalFees = parseOptionalFloat(tx.actual_total_fees_usd)
                            ?? ((parseOptionalFloat(tx.actual_merchant_fee_usd) ?? 0)
                              + (parseOptionalFloat(tx.actual_customer_fee_usd) ?? 0));
                          if (totalFees === null || totalFees === 0) {
                            return <span className="text-muted">—</span>;
                          }
                          const feeClass = tx.resolution_action === 'deny' ? 'text-danger' : 'text-muted';
                          return <span className={feeClass}>{formatCurrency(totalFees)}</span>;
                        })()}
                      </td>
                    )}
                    {/* Net Payout column - Merchant Admin and FI Admin */}
                    {(isMerchantAdmin || (isFIAdmin && !isSuperAdmin)) && (
                      <td className="td-amount text-right">
                        {(() => {
                          const netPayout = getNetPayout(tx);
                          if (netPayout === null) {
                            return <span className="text-muted">Pending</span>;
                          }
                          if (Math.abs(netPayout) <= AMOUNT_DISPLAY_THRESHOLD) {
                            return <span className="text-muted">$0.00</span>;
                          }
                          return (
                            <span className={`payout-badge ${netPayout < 0 ? 'payout-badge--negative' : 'payout-badge--positive'}`}>
                              {formatCurrency(netPayout)}
                            </span>
                          );
                        })()}
                      </td>
                    )}
                    <td>
                      <span className={`status-badge status-badge--${tx.status}`}>
                        {tx.status_display || tx.status}
                      </span>
                      {hasSettlementShortfall && (
                        <span className="status-badge status-badge--shortfall" title="Settlement shortfall vs sales amount">
                          Shortfall
                        </span>
                      )}
                      {showSettlementPending && (
                        <span className="status-badge status-badge--pending" title="Settlement pending">
                          Settlement Pending
                        </span>
                      )}
                      {tx.was_late_payment && (
                        <span className="status-badge status-badge--late" title={tx.late_by_formatted || `${tx.late_by_seconds}s late`}>
                          LATE
                        </span>
                      )}
                      {hasRefundDue && (
                        <span
                          className="status-badge status-badge--refund-due"
                          title={refundDueSource === 'overage'
                            ? 'Overage refund pending - click to complete'
                            : 'Refund pending - click to complete'}
                        >
                          Refund Due
                        </span>
                      )}
                      {isFullyRefunded && !hasRefundDue && tx.status !== 'refunded' && (
                        <span className="status-badge status-badge--refunded" title="Fully refunded">
                          Refunded
                        </span>
                      )}
                      {/* Show refund badge for manual refunds when not fully refunded */}
                      {hasCompletedManualRefund && !isFullyRefunded && !hasRefundDue && tx.status !== 'refunded' && (
                        <span className="status-badge status-badge--refunded" title={`Refunded ${formatCurrency(manualRefundsTotal)}`}>
                          Refunded
                        </span>
                      )}
                    </td>
                    {/* Difference column - hidden for FI Admins */}
                    {!isFIAdmin && (
                      <td className="td-variance text-right">
                        {(() => {
                          if (tx.resolution_action === 'deny') {
                            return null;
                          }
                          // For refunded transactions, show nothing in Difference column
                          if (tx.status === 'refunded') {
                            return null;
                          }

                          // For paid status with partial_refund resolution where refund is complete,
                          // don't show difference - the overage has been refunded
                          if ((tx.status === 'paid' || tx.status === 'refund_pending')
                              && tx.resolution_action === 'partial_refund'
                              && !hasRefundDue) {
                            return null;
                          }

                          // For keep_full resolution, show the overage kept in green
                          // Compare against expected payment (includes customer fees), not just order amount
                          if (tx.resolution_action === 'keep_full' && tx.accepted_amount_usd) {
                            const expectedUsd = parseFloat(tx.expected_payment_usd) || parseFloat(tx.order_amount_usd) || 0;
                            const overage = parseFloat(tx.accepted_amount_usd) - expectedUsd;
                            if (overage > AMOUNT_DISPLAY_THRESHOLD) {
                              return (
                                <span className="variance-badge variance-badge--positive">
                                  +{formatCurrency(overage)}
                                </span>
                              );
                            }
                          }

                          if (isSuperAdmin) {
                            // For discrepancy statuses, use crypto mismatch (quote-based)
                            if (DISCREPANCY_STATUSES.includes(tx.status)) {
                              const mismatch = getCryptoMismatchUsd(tx);
                              const discrepancy = mismatch !== null
                                ? mismatch
                                : (tx.payment_variance_usd !== null && tx.payment_variance_usd !== undefined
                                  ? parseFloat(tx.payment_variance_usd)
                                  : null);
                              if (discrepancy !== null && discrepancy !== 0) {
                                // Use orange "action" style for overfunded (needs resolution)
                                // Use red for underfunded, green only for resolved/keeping
                                const badgeClass = (tx.status === 'needs_resolution' && tx.discrepancy_type === 'overfunded')
                                  ? 'variance-badge--action'
                                  : (discrepancy >= 0 ? 'variance-badge--positive' : 'variance-badge--negative');
                                return (
                                  <span className={`variance-badge ${badgeClass}`}>
                                    {discrepancy >= 0 ? '+' : ''}{formatCurrency(discrepancy)}
                                  </span>
                                );
                              }
                            }

                            // For paid status, only show discrepancy if there actually is one
                            if (tx.status === 'paid') {
                              // Use discrepancy_type as primary check (semantic), fall back to raw crypto field
                              const hasCryptoDiscrepancy = (tx.discrepancy_type && tx.discrepancy_type !== 'none') ||
                                (tx.discrepancy_amount_crypto !== null && tx.discrepancy_amount_crypto !== undefined);
                              if (hasCryptoDiscrepancy) {
                                const mismatch = getCryptoMismatchUsd(tx);
                                const discrepancy = mismatch !== null
                                  ? mismatch
                                  : (tx.payment_variance_usd !== null && tx.payment_variance_usd !== undefined
                                    ? parseFloat(tx.payment_variance_usd)
                                    : null);
                                if (discrepancy !== null && Math.abs(discrepancy) >= 0.01) {
                                  return (
                                    <span className={`variance-badge ${discrepancy >= 0 ? 'variance-badge--positive' : 'variance-badge--negative'}`}>
                                      {discrepancy >= 0 ? '+' : ''}{formatCurrency(discrepancy)}
                                    </span>
                                  );
                                }
                              }
                              // No discrepancy - show nothing
                              return null;
                            }

                            // Other statuses
                            return <span className="variance-badge variance-badge--neutral">—</span>;
                          }

                          if (isMerchantAdmin) {
                            if (DISCREPANCY_STATUSES.includes(tx.status)) {
                              // Calculate discrepancy from expected vs received (works even when crypto fields stripped)
                              const expectedUsd = parseOptionalFloat(tx.expected_payment_usd)
                                ?? parseOptionalFloat(tx.order_amount_usd)
                                ?? 0;
                              const receivedUsd = getSettlementUsd(tx) ?? 0;
                              const discrepancy = receivedUsd - expectedUsd;

                              if (Math.abs(discrepancy) >= 0.01) {
                                // Use orange "action" style for overfunded (needs resolution)
                                // Use red for underfunded, green only for resolved/keeping
                                const badgeClass = (tx.status === 'needs_resolution' && tx.discrepancy_type === 'overfunded')
                                  ? 'variance-badge--action'
                                  : (discrepancy >= 0 ? 'variance-badge--positive' : 'variance-badge--negative');
                                return (
                                  <span className={`variance-badge ${badgeClass}`}>
                                    {discrepancy >= 0 ? '+' : ''}{formatCurrency(discrepancy)}
                                  </span>
                                );
                              }
                              return <span className="variance-badge variance-badge--neutral">—</span>;
                            }

                            if (tx.status === 'paid') {
                              // Check for crypto discrepancy FIRST (underfunded/overfunded payments)
                              // This takes precedence over settlement shortfall when discrepancy_type is set
                              const hasCryptoDiscrepancy = (tx.discrepancy_type && tx.discrepancy_type !== 'none') ||
                                (tx.discrepancy_amount_crypto !== null && tx.discrepancy_amount_crypto !== undefined);
                              if (hasCryptoDiscrepancy) {
                                // Calculate discrepancy from expected payment vs received (works for merchants)
                                const expectedUsd = parseOptionalFloat(tx.expected_payment_usd)
                                  ?? parseOptionalFloat(tx.order_amount_usd)
                                  ?? 0;
                                const receivedUsd = getSettlementUsd(tx) ?? 0;
                                const discrepancy = receivedUsd - expectedUsd;
                                if (Math.abs(discrepancy) > AMOUNT_DISPLAY_THRESHOLD) {
                                  const isPositive = discrepancy > 0;
                                  return (
                                    <span className={`variance-badge ${isPositive ? 'variance-badge--positive' : 'variance-badge--negative'}`}>
                                      {isPositive ? '+' : ''}{formatCurrency(discrepancy)}
                                    </span>
                                  );
                                }
                              }
                              // Show settlement shortfall (rate slippage - crypto matched but USD settled lower)
                              // Only shown when there's no crypto discrepancy
                              if (hasSettlementShortfall) {
                                return (
                                  <span className="variance-badge variance-badge--negative">
                                    -{formatCurrency(merchantShortfallUsd)}
                                  </span>
                                );
                              }
                              // No discrepancy - show nothing
                              return null;
                            }

                            return <span className="variance-badge variance-badge--neutral">—</span>;
                          }

                          return <span className="variance-badge variance-badge--neutral">—</span>;
                        })()}
                      </td>
                    )}
                    {/* Commission column - shown for FI Admin only */}
                    {isFIAdmin && !isSuperAdmin && (
                      <td className="td-commission text-right">
                        {tx.status === 'paid' ? formatCurrency(calculateCommission(tx)) : '-'}
                      </td>
                    )}
                    <td className="td-date">
                      {moment(tx.created_at).format('MM/DD/YYYY h:mm:ss A')}
                    </td>
                  </tr>
                  );
                })}
              </tbody>
            </table>

            {/* Pagination */}
            {totalCount > transactions.length && (
              <div className="table-footer">
                <div className="pagination-info">
                  Showing <strong>{transactions.length}</strong> of <strong>{formatNumber(totalCount)}</strong>{' '}
                  transactions
                </div>
                <div className="pagination-controls">
                  <button
                    className="pagination-btn"
                    disabled={currentPage === 1}
                    onClick={() => setCurrentPage((p) => p - 1)}
                  >
                    Previous
                  </button>
                  <button
                    className="pagination-btn"
                    disabled={!listData?.next}
                    onClick={() => setCurrentPage((p) => p + 1)}
                  >
                    Next
                  </button>
                </div>
              </div>
            )}
          </>
        )}
      </div>

      {/* Transaction Drawer */}
      <TransactionDrawer
        transaction={selectedTransaction}
        isOpen={drawerOpen}
        onClose={handleCloseDrawer}
        user={user}
        token={accessToken}
        onResolve={handleRefreshData}
      />
    </div>
  );
};

TransactionsListPage.propTypes = {
  setBreadcrumbs: propTypes.func.isRequired,
  token: propTypes.object,
  user: propTypes.object,
  financialInstitutions: propTypes.object,
  fetchFIs: propTypes.func.isRequired,
  summaryData: propTypes.object,
  summaryLoading: propTypes.bool,
  summaryError: propTypes.string,
  listData: propTypes.object,
  listLoading: propTypes.bool,
  listError: propTypes.string,
  fetchTransactionSummary: propTypes.func.isRequired,
  fetchTransactionList: propTypes.func.isRequired,
  fetchFITransactionSummary: propTypes.func.isRequired,
  fetchFITransactionList: propTypes.func.isRequired,
  fetchAllTransactionsSummary: propTypes.func.isRequired,
  fetchAllTransactionsList: propTypes.func.isRequired,
  externalDateRange: propTypes.array,
  hideDatePicker: propTypes.bool,
  showInlineHeader: propTypes.bool,
};

function mapStateToProps(state) {
  return {
    token: getToken(state),
    user: getUser(state),
    financialInstitutions: getFIs(state),
    summaryData: state.transaction?.summary?.data,
    summaryLoading: state.transaction?.summary?.loading,
    summaryError: state.transaction?.summary?.error,
    listData: state.transaction?.list?.data,
    listLoading: state.transaction?.list?.loading,
    listError: state.transaction?.list?.error,
  };
}

const connectedTransactionsListPage = connect(mapStateToProps, {
  setBreadcrumbs,
  fetchFIs,
  fetchTransactionSummary,
  fetchTransactionList,
  fetchFITransactionSummary,
  fetchFITransactionList,
  fetchAllTransactionsSummary,
  fetchAllTransactionsList,
})(TransactionsListPage);

export { connectedTransactionsListPage as TransactionsListPage };
